import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import TrophyIcon from "@/components/icons/trophy-icon";
import LexiTipsLogo from "@/assets/lexi-tips-logo.svg";
import Trophy from "@/assets/trophy.svg";

export default function TipWinnerModal({ entry, onClose }) {
  const [isVisible, setIsVisible] = useState(false);
  const confettiCanvasRef = useRef(null);

  useEffect(() => {
    // Trigger entrance animation after component mounts
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    // Prevent body scrolling when modal is open
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none"; // Prevent touch actions too

    // Start confetti animation
    const startConfetti = () => {
      if (typeof window !== 'undefined' && window.confetti && confettiCanvasRef.current) {
        // Create a confetti instance with our canvas
        const myConfetti = window.confetti.create(confettiCanvasRef.current, {
          resize: true,
          useWorker: true
        });
        
        const duration = 5 * 1000; // Extend duration to 30 seconds
        const animationEnd = Date.now() + duration;
        let lastBurstTime = 0;
        
        const runConfettiAnimation = () => {
          const currentTime = Date.now();
          const timeLeft = animationEnd - currentTime;
          
          if (timeLeft <= 0) return;
          
          // Only create new bursts every 700ms for a slower, gentler effect
          if (currentTime - lastBurstTime > 700) {
            lastBurstTime = currentTime;
            
            // Randomize colors
            const colors = [
              '#FFF', // white
              '#FFD700', // gold
              '#F0F', // magenta
              '#0FF', // cyan
              '#FF0', // yellow
              '#ff1493', // deep pink (match background)
            ];
            
            // Launch confetti from different directions with more particles
            myConfetti({
              particleCount: 4,
              angle: 60,
              spread: 55,
              origin: { x: 0, y: 0.7 },
              colors: colors,
              startVelocity: 25, // Reduced velocity
              gravity: 0.5, // Reduced gravity
              shapes: ['square', 'circle'],
              scalar: 1.2,
              ticks: 300, // Increased ticks for longer-lasting particles
              zIndex: 9999
            });
            
            myConfetti({
              particleCount: 4,
              angle: 120,
              spread: 55,
              origin: { x: 1, y: 0.7 },
              colors: colors,
              startVelocity: 25, // Reduced velocity
              gravity: 0.5, // Reduced gravity
              shapes: ['square', 'circle'],
              scalar: 1.2,
              ticks: 300, // Increased ticks for longer-lasting particles
              zIndex: 9999
            });
            
            myConfetti({
              particleCount: 4,
              angle: 90,
              spread: 80,
              origin: { x: 0.5, y: 0.3 },
              colors: colors,
              startVelocity: 20, // Reduced velocity
              gravity: 0.5, // Reduced gravity
              shapes: ['square', 'circle'],
              scalar: 1.2,
              ticks: 300, // Increased ticks for longer-lasting particles
              zIndex: 9999
            });
          }
          
          requestAnimationFrame(runConfettiAnimation);
        };
        
        runConfettiAnimation();
        
        return myConfetti;
      }
      return null;
    };
    
    // Delay starting the confetti slightly to ensure the modal is visible
    const confettiTimer = setTimeout(() => {
      const myConfetti = startConfetti();
      return () => {
        if (myConfetti) myConfetti.reset();
      };
    }, 300);

    return () => {
      clearTimeout(timer);
      clearTimeout(confettiTimer);
      document.body.style.overflow = "auto";
      document.body.style.touchAction = "auto";
    };
  }, []);

  const handleClose = () => {
    setIsVisible(false);

    // Clear localStorage state
    try {
      localStorage.removeItem("winner_modal_open");
      localStorage.removeItem("winner_modal_data");
      // Dispatch event to notify listeners
      window.dispatchEvent(new Event("winner_modal_update"));
    } catch (error) {
      console.error("Error clearing winner modal data:", error);
    }

    // Call onClose after a short delay to allow exit animation
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  if (!entry) return null;

  // Use React Portal to render at the document body level
  return createPortal(
    <div
      className={`fixed top-0 left-0 right-0 bottom-0 w-screen h-screen text-white z-[9999] flex flex-col items-center justify-center overflow-hidden transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"}`}
      style={{
        background: 'linear-gradient(to bottom, #ff1493 0%, #ff1493 50%, rgba(150, 0, 70, 0.9) 100%)'
      }}
    >
      {/* Canvas for confetti - must be at the top level inside the modal */}
      <canvas 
        ref={confettiCanvasRef}
        className="fixed inset-0 w-full h-full pointer-events-none z-[10]"
        style={{ position: 'fixed' }}
      />
      
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 text-white bg-transparent border border-white/50 rounded-md px-3 py-1 text-sm font-black"
        aria-label="Close"
      >
        CLOSE
      </button>

      <div className="text-center relative z-10 w-full max-w-4xl px-4">
        <h1 className="text-[64px] font-black tracking-wideitalic italic">WINNER</h1>

        <div className="flex flex-col items-center">
          <h2 className="text-[32px] font-black z-10 italic leading-[32px]">
            {entry.trackName}
          </h2>
          <h3 className="text-[24px] font-black z-10 italic">
            Race {entry.raceNumber}
          </h3>
          <h3 className="text-[24px] font-black z-10">#{entry.number}</h3>
        </div>

        <div className="mb-6 flex gap-2 justify-center flex-row items-center">
          {entry.silks && (
            <div className="silks-container w-[64px] h-[64px] bg-white rounded-full p-2 border-4 border-white">
              <img
                src={entry.silks}
                alt="Jockey silk"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}
          <h2 className="text-[32px] font-black text-nowrap">{entry.name}</h2>
        </div>

        <div className="justify-center flex mb-24">
          <img src={Trophy} alt="Trophy" className="w-24 h-24" />
        </div>
      </div>

      <div className="absolute bottom-0 pb-10 pt-12 left-0 right-0 text-center bg-white">
        <img
          src={LexiTipsLogo}
          alt="Lexi.tips"
          className="w-56 h-24 mx-auto object-cover scale-[120%]"
        />
      </div>
    </div>,
    document.body,
  );
}
